import React, { useState, useRef, useEffect } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Card as CardType, Label } from '../types/board';
import WordPressPostModal from './WordPressPostModal';
import './Card.css';

interface CardProps {
  card: CardType;
  index: number;
  labels: { [key: string]: Label };
  onDelete: (cardId: string) => void;
  owners: string[];
  onUpdateOwner: (cardId: string, newOwner: string) => void;
  onUpdateDueDate: (cardId: string, newDueDate: string) => void;
  onUpdateLocalTitle: (cardId: string, newLocalTitle: string) => void;
  onUpdateLocalPlan: (cardId: string, newLocalPlan: string) => void;
}

const Card: React.FC<CardProps> = ({ card, index, labels, onDelete, owners, onUpdateOwner, onUpdateDueDate, onUpdateLocalTitle, onUpdateLocalPlan }) => {
  const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isEditingLocalTitle, setIsEditingLocalTitle] = useState(false);
  const [localTitleInput, setLocalTitleInput] = useState(card.localTitle || '');
  const [localPlanInput, setLocalPlanInput] = useState(card.localPlan || '');
  const [showPostModal, setShowPostModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const localTitleInputRef = useRef<HTMLInputElement>(null);
  const localPlanTextareaRef = useRef<HTMLTextAreaElement>(null);
  const cardLabels = card.labelIds
    .map((labelId) => labels[labelId])
    .filter(Boolean);

  const isWordPressCard = !!card.wordpressPostId;

  const getDueDateStatus = (dueDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'today';
    if (diffDays <= 3) return 'soon';
    return 'upcoming';
  };

  const formatDueDate = (dueDate: string) => {
    const date = new Date(dueDate);
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOwnerDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showOwnerDropdown || showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOwnerDropdown, showDatePicker]);

  const handleOwnerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOwnerDropdown(!showOwnerDropdown);
  };

  const handleOwnerChange = (newOwner: string) => {
    onUpdateOwner(card.id, newOwner);
    setShowOwnerDropdown(false);
  };

  const handleDateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDatePicker(!showDatePicker);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    if (newDate) {
      onUpdateDueDate(card.id, newDate);
      setShowDatePicker(false);
    }
  };

  const handleClearDate = () => {
    onUpdateDueDate(card.id, '');
    setShowDatePicker(false);
  };

  const handleLocalTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingLocalTitle(true);
  };

  const handleLocalTitleBlur = () => {
    if (localTitleInput !== card.localTitle) {
      onUpdateLocalTitle(card.id, localTitleInput);
    }
    setIsEditingLocalTitle(false);
  };

  const handleLocalTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLocalTitleBlur();
    } else if (e.key === 'Escape') {
      setLocalTitleInput(card.localTitle || '');
      setIsEditingLocalTitle(false);
    }
  };

  const handleLocalPlanBlur = () => {
    if (localPlanInput !== card.localPlan) {
      onUpdateLocalPlan(card.id, localPlanInput);
    }
  };

  const handleLocalPlanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setLocalPlanInput(card.localPlan || '');
      localPlanTextareaRef.current?.blur();
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingLocalTitle && localTitleInputRef.current) {
      localTitleInputRef.current.focus();
      localTitleInputRef.current.select();
    }
  }, [isEditingLocalTitle]);

  return (
    <>
    <Draggable draggableId={card.id} index={index} isDragDisabled={isWordPressCard}>
      {(provided, snapshot) => (
        <div
          className={`card ${snapshot.isDragging ? 'dragging' : ''} ${isWordPressCard ? 'wordpress-card' : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {/* Top Row: Site Label | Local Title */}
          <div className="card-top-row">
            <div className="card-top-left">
              {cardLabels.length > 0 && (
                <div className="card-labels">
                  {cardLabels.map((label) => (
                    <span
                      key={label.id}
                      className="card-label"
                      style={{ backgroundColor: label.color }}
                      title={label.name}
                    >
                      {label.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="card-local-title-wrapper">
                {isEditingLocalTitle ? (
                  <input
                    ref={localTitleInputRef}
                    type="text"
                    className="card-local-title-input"
                    value={localTitleInput}
                    onChange={(e) => setLocalTitleInput(e.target.value)}
                    onBlur={handleLocalTitleBlur}
                    onKeyDown={handleLocalTitleKeyDown}
                    placeholder="Add local title..."
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div
                    className="card-local-title clickable"
                    onClick={handleLocalTitleClick}
                    title="Click to edit local title"
                  >
                    {card.localTitle || <span className="local-title-placeholder">+ Local title</span>}
                  </div>
                )}
              </div>
            </div>
            <button
              className="card-delete"
              onClick={() => onDelete(card.id)}
              aria-label="Delete card"
            >
              ×
            </button>
          </div>

          {/* Second Row: Owner | Date */}
          <div className="card-meta-row">
            <div className="card-owner-wrapper" ref={dropdownRef}>
              <div
                className="card-owner clickable"
                onClick={handleOwnerClick}
                title="Click to change owner"
              >
                <span className="owner-icon">👤</span>
                <span className="owner-name">{card.owner || 'Unassigned'}</span>
              </div>
              {showOwnerDropdown && (
                <div className="owner-dropdown">
                  <div className="owner-dropdown-header">Assign to:</div>
                  {owners.map((owner) => (
                    <div
                      key={owner}
                      className={`owner-dropdown-item ${card.owner === owner ? 'selected' : ''}`}
                      onClick={() => handleOwnerChange(owner)}
                    >
                      <span className="owner-icon">👤</span>
                      <span>{owner}</span>
                    </div>
                  ))}
                  {card.owner && (
                    <div
                      className="owner-dropdown-item unassign"
                      onClick={() => handleOwnerChange('')}
                    >
                      <span className="owner-icon">✕</span>
                      <span>Unassign</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="card-date-wrapper" ref={datePickerRef}>
              {card.dueDate ? (
                <div
                  className={`card-due-date clickable ${getDueDateStatus(card.dueDate)}`}
                  onClick={handleDateClick}
                  title="Click to change due date"
                >
                  <span className="due-date-icon">📅</span>
                  <span className="due-date-text">{formatDueDate(card.dueDate)}</span>
                </div>
              ) : (
                <div
                  className="card-due-date no-date clickable"
                  onClick={handleDateClick}
                  title="Click to set due date"
                >
                  <span className="due-date-icon">📅</span>
                  <span className="due-date-text">No date</span>
                </div>
              )}
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <div className="date-picker-header">Set due date:</div>
                  <input
                    type="date"
                    className="date-picker-input"
                    defaultValue={card.dueDate || ''}
                    onChange={handleDateChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {card.dueDate && (
                    <button
                      className="date-picker-clear"
                      onClick={handleClearDate}
                    >
                      Clear date
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* WordPress Title & Description - Light Blue Section */}
          <div className="card-content-section">
            {/* Local Plan - Planning notes */}
            <div className="card-local-plan-wrapper">
              <textarea
                ref={localPlanTextareaRef}
                className="card-local-plan-textarea"
                placeholder="+ Local plan"
                value={localPlanInput}
                onChange={(e) => setLocalPlanInput(e.target.value)}
                onBlur={handleLocalPlanBlur}
                onKeyDown={handleLocalPlanKeyDown}
                rows={3}
              />
            </div>

            <div className="card-header">
              <h4>{card.title}</h4>
              {isWordPressCard && (
                <button
                  className="card-view-post-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPostModal(true);
                  }}
                  title="View full WordPress post"
                >
                  📄
                </button>
              )}
            </div>
            {card.description && (
              <p className="card-description">{card.description}</p>
            )}
          </div>
        </div>
      )}
    </Draggable>

    {/* WordPress Post Modal */}
    {showPostModal && card.wordpressPostId && (
      <WordPressPostModal
        wordpressPostId={card.wordpressPostId}
        onClose={() => setShowPostModal(false)}
      />
    )}
    </>
  );
};

export default Card;
